<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import Chart from "chart.js/auto";
import STATUS_TONE_MAP from "@/Constans/StatusToneMap";

const props = defineProps({
  overview: { type: Object, default: () => ({}) },
  incidences: { type: Array, default: () => [] },
  attemptsDistribution: { type: Array, default: () => [] },
});

const stopsStatusCanvasRef = ref(null);
const serviceDistributionCanvasRef = ref(null);
const qualityCanvasRef = ref(null);
const incidencesCanvasRef = ref(null);
const attemptsDistCanvasRef = ref(null);

let charts = [];

const stopsStatusData = computed(() => {
  const o = props.overview || {};
  return {
    labels: ["Finalizadas", "Parciales", "Incidencias", "Asignadas", "No asignadas", "Excluidas"],
    values: [
      toNumber(o.completed_stops),
      toNumber(o.partial_stops),
      toNumber(o.incidence_stops),
      toNumber(o.assigned_stops),
      toNumber(o.unassigned_stops),
      toNumber(o.cancelled_stops),
    ],
    colors: [
      STATUS_TONE_MAP.finalizado.border,
      STATUS_TONE_MAP.en_curso.border,
      STATUS_TONE_MAP.incidencia.border,
      STATUS_TONE_MAP.pendiente.border,
      STATUS_TONE_MAP.cancelado.border,
      "#9ca3af",
    ],
  };
});

const serviceDistributionData = computed(() => {
  const o = props.overview || {};
  return {
    labels: ["Entregas", "Recogidas"],
    values: [toNumber(o.total_deliveries), toNumber(o.total_pickups)],
    total: toNumber(o.total_stops),
  };
});

const qualityData = computed(() => {
  const o = props.overview || {};
  return {
    labels: ["Primer intento", "Éxito total", "Incidencias"],
    values: [
      toNumber(o.first_attempt_success_rate),
      toNumber(o.success_rate),
      toNumber(o.incidence_rate),
    ],
    colors: [
      STATUS_TONE_MAP.en_curso.border,
      STATUS_TONE_MAP.finalizado.border,
      STATUS_TONE_MAP.incidencia.border,
    ],
  };
});

const topIncidencesData = computed(() =>
  [...(Array.isArray(props.incidences) ? props.incidences : [])]
    .sort((left, right) => toNumber(right?.total) - toNumber(left?.total))
    .slice(0, 5)
);

const attemptsDistData = computed(() => {
  const items = Array.isArray(props.attemptsDistribution) ? props.attemptsDistribution : [];
  return {
    labels: items.map((item) => `${item.attempts} intento${item.attempts === 1 ? "" : "s"}`),
    values: items.map((item) => toNumber(item.total)),
    colors: [
      STATUS_TONE_MAP.finalizado.border,
      STATUS_TONE_MAP.pendiente.border,
      STATUS_TONE_MAP.incidencia.border,
    ],
  };
});

const hasIncidences = computed(() => topIncidencesData.value.length > 0);
const hasAttemptsDistribution = computed(() =>
  attemptsDistData.value.values.some((v) => v > 0)
);

onMounted(() => {
  renderCharts();
});

watch(
  () => [props.overview, props.incidences, props.attemptsDistribution],
  () => {
    renderCharts();
  },
  { deep: true }
);

onBeforeUnmount(() => destroyCharts());

async function renderCharts() {
  await nextTick();
  destroyCharts();
  charts = [
    buildStopsStatusChart(),
    buildServiceDistributionChart(),
    buildQualityChart(),
    buildTopIncidencesChart(),
    buildAttemptsDistributionChart(),
  ].filter(Boolean);
}

function buildStopsStatusChart() {
  const canvas = stopsStatusCanvasRef.value;
  if (!canvas) return null;
  return new Chart(canvas.getContext("2d"), {
    type: "bar",
    data: {
      labels: stopsStatusData.value.labels,
      datasets: [
        {
          data: stopsStatusData.value.values,
          backgroundColor: stopsStatusData.value.colors,
          borderRadius: 10,
          barThickness: 20,
        },
      ],
    },
    options: baseVerticalOptions((value) => formatInteger(value)),
  });
}

function buildServiceDistributionChart() {
  const canvas = serviceDistributionCanvasRef.value;
  if (!canvas) return null;
  const total = serviceDistributionData.value.total;
  return new Chart(canvas.getContext("2d"), {
    type: "doughnut",
    data: {
      labels: serviceDistributionData.value.labels,
      datasets: [
        {
          data: serviceDistributionData.value.values,
          backgroundColor: [STATUS_TONE_MAP.en_curso.border, STATUS_TONE_MAP.cancelado.border],
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: {
          position: "bottom",
          labels: { usePointStyle: true, pointStyle: "circle" },
        },
        tooltip: {
          callbacks: {
            label(context) {
              const value = toNumber(context.parsed);
              const percent = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
              return `${context.label}: ${formatInteger(value)} (${percent}%)`;
            },
          },
        },
      },
    },
    plugins: [centerTextPlugin(total)],
  });
}

function buildQualityChart() {
  const canvas = qualityCanvasRef.value;
  if (!canvas) return null;
  return new Chart(canvas.getContext("2d"), {
    type: "bar",
    data: {
      labels: qualityData.value.labels,
      datasets: [
        {
          data: qualityData.value.values,
          backgroundColor: qualityData.value.colors,
          borderRadius: 10,
          barThickness: 18,
        },
      ],
    },
    options: baseHorizontalPercentOptions(),
  });
}

function buildTopIncidencesChart() {
  const canvas = incidencesCanvasRef.value;
  if (!canvas) return null;
  if (!hasIncidences.value) return null;
  return new Chart(canvas.getContext("2d"), {
    type: "bar",
    data: {
      labels: topIncidencesData.value.map((item) => item.status_description),
      datasets: [
        {
          data: topIncidencesData.value.map((item) => toNumber(item.total)),
          backgroundColor: STATUS_TONE_MAP.incidencia.border,
          borderRadius: 10,
          barThickness: 18,
        },
      ],
    },
    options: baseHorizontalOptions(),
  });
}

function buildAttemptsDistributionChart() {
  const canvas = attemptsDistCanvasRef.value;
  if (!canvas) return null;
  if (!hasAttemptsDistribution.value) return null;
  return new Chart(canvas.getContext("2d"), {
    type: "bar",
    data: {
      labels: attemptsDistData.value.labels,
      datasets: [
        {
          data: attemptsDistData.value.values,
          backgroundColor: attemptsDistData.value.colors,
          borderRadius: 10,
          barThickness: 28,
        },
      ],
    },
    options: baseVerticalOptions((value) => formatInteger(value)),
  });
}

function destroyCharts() {
  charts.forEach((chart) => chart?.destroy?.());
  charts = [];
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatInteger(value) {
  return new Intl.NumberFormat("es-ES").format(toNumber(value));
}

function baseVerticalOptions(tooltipFormatter) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label(context) {
            return `${context.dataset.label || "Valor"}: ${tooltipFormatter(context.parsed.y ?? context.parsed)}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };
}

function baseHorizontalPercentOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label(context) {
            return `${context.label}: ${Number(context.parsed.x || 0).toFixed(1)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      y: { grid: { display: false } },
    },
  };
}

function baseHorizontalOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label(context) {
            return `${context.label}: ${formatInteger(context.parsed.x)}`;
          },
        },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
      y: { grid: { display: false } },
    },
  };
}

function centerTextPlugin(total) {
  return {
    id: "centerText",
    afterDraw(chart) {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      const centerX = (chartArea.left + chartArea.right) / 2;
      const centerY = (chartArea.top + chartArea.bottom) / 2;
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#111827";
      ctx.font = "600 18px sans-serif";
      ctx.fillText(formatInteger(total), centerX, centerY - 6);
      ctx.fillStyle = "#6b7280";
      ctx.font = "500 11px sans-serif";
      ctx.fillText("Paradas", centerX, centerY + 14);
      ctx.restore();
    },
  };
}
</script>

<template>
  <div class="row g-3">
    <div class="col-12 col-xl-6">
      <div class="card card-detail-layout h-100">
        <div class="card-header pb-0">
          <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-0">Estado de las paradas</p>
        </div>
        <div class="card-body analytics-chart-body">
          <div class="analytics-chart-canvas-wrap">
            <canvas ref="stopsStatusCanvasRef" />
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-xl-6">
      <div class="card card-detail-layout h-100">
        <div class="card-header pb-0">
          <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-0">Distribución del servicio</p>
        </div>
        <div class="card-body analytics-chart-body">
          <div class="analytics-chart-canvas-wrap">
            <canvas ref="serviceDistributionCanvasRef" />
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-xl-6">
      <div class="card card-detail-layout h-100">
        <div class="card-header pb-0">
          <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-0">Calidad operativa</p>
        </div>
        <div class="card-body analytics-chart-body">
          <div class="analytics-chart-canvas-wrap">
            <canvas ref="qualityCanvasRef" />
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-xl-6">
      <div class="card card-detail-layout h-100">
        <div class="card-header pb-0">
          <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-0">Motivos de incidencia más frecuentes</p>
        </div>
        <div class="card-body analytics-chart-body">
          <template v-if="hasIncidences">
            <div class="analytics-chart-canvas-wrap">
              <canvas ref="incidencesCanvasRef" />
            </div>
          </template>
          <div v-else class="text-sm text-secondary py-5 text-center">
            Sin incidencias en el periodo
          </div>
        </div>
      </div>
    </div>

    <div class="col-12">
      <div class="card card-detail-layout">
        <div class="card-header pb-0">
          <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-0">Distribución de intentos</p>
        </div>
        <div class="card-body analytics-chart-body">
          <template v-if="hasAttemptsDistribution">
            <div class="analytics-chart-canvas-wrap">
              <canvas ref="attemptsDistCanvasRef" />
            </div>
          </template>
          <div v-else class="text-sm text-secondary py-5 text-center">
            Sin datos de intentos en el periodo
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.analytics-chart-body {
  height: 16rem;
  min-height: 16rem;
  max-height: 16rem;
}

.analytics-chart-canvas-wrap {
  height: 100%;
  position: relative;
  width: 100%;
}

.analytics-chart-canvas-wrap canvas {
  width: 100% !important;
  height: 100% !important;
}
</style>
